if (!customElements.get('islander-workshop-carousel')) {
  class IslanderWorkshopCarousel extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;

      this.abortController = new AbortController();
      this.track = this.querySelector('[data-workshop-track]');
      this.status = this.querySelector('[data-workshop-status]');
      this.slides = Array.from(this.querySelectorAll('[data-workshop-slide]'));
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.speed = Math.min(24, Math.max(6, Number(this.dataset.workshopSpeed) || 16));
      this.isLooping = this.hasAttribute('data-workshop-loop') && this.slides.length > 1;
      this.pauseReasons = new Set();
      this.lastFrameTime = null;
      this.dragState = null;
      this.virtualPosition = this.track?.scrollLeft || 0;
      this.slideDistance = 0;
      this.loopSpan = 0;

      if (!this.track || this.slides.length === 0) return;

      if (this.isLooping) this.setupLoop();
      this.refreshMeasurements();
      if (this.isLooping) this.setLoopStart();

      const { signal } = this.abortController;
      this.track.addEventListener(
        'scroll',
        () => {
          if (this.pauseReasons.size) this.virtualPosition = this.track.scrollLeft;
          this.queueUpdate();
        },
        { passive: true, signal },
      );
      this.addEventListener(
        'pointerenter',
        (event) => {
          if (event.pointerType === 'mouse') this.setPaused('hover', true);
        },
        { signal },
      );
      this.addEventListener(
        'pointerleave',
        (event) => {
          if (event.pointerType === 'mouse') this.setPaused('hover', false);
        },
        { signal },
      );
      this.addEventListener('focusin', () => this.setPaused('focus', true), { signal });
      this.addEventListener(
        'focusout',
        (event) => {
          if (!this.contains(event.relatedTarget)) this.setPaused('focus', false);
        },
        { signal },
      );

      this.track.addEventListener('pointerdown', (event) => this.onPointerDown(event), { signal });
      this.track.addEventListener('pointermove', (event) => this.onPointerMove(event), { signal });
      this.track.addEventListener('pointerup', (event) => this.onPointerEnd(event), { signal });
      this.track.addEventListener('pointercancel', (event) => this.onPointerEnd(event), { signal });
      this.track.addEventListener('lostpointercapture', (event) => this.onPointerEnd(event), { signal });
      this.track.addEventListener('wheel', () => this.onWheel(), { passive: true, signal });

      document.addEventListener(
        'visibilitychange',
        () => this.setPaused('hidden', document.hidden),
        { signal },
      );
      document.addEventListener(
        'shopify:block:select',
        (event) => {
          if (this.contains(event.target)) this.setPaused('editor', true);
        },
        { signal },
      );
      document.addEventListener(
        'shopify:block:deselect',
        (event) => {
          if (this.contains(event.target)) this.setPaused('editor', false);
        },
        { signal },
      );
      window.addEventListener(
        'resize',
        () => {
          this.refreshMeasurements();
          this.normalizePosition();
          this.queueUpdate();
        },
        { passive: true, signal },
      );

      this.handleMotionChange = () => this.setPaused('reduced-motion', this.reducedMotionQuery.matches);
      this.reducedMotionQuery.addEventListener?.('change', this.handleMotionChange);
      this.setPaused('reduced-motion', this.reducedMotionQuery.matches);
      this.setPaused('hidden', document.hidden);
      this.update();
      this.start();
    }

    disconnectedCallback() {
      window.cancelAnimationFrame(this.animationFrame);
      window.cancelAnimationFrame(this.updateFrame);
      window.clearTimeout(this.resumeTimer);
      this.reducedMotionQuery?.removeEventListener?.('change', this.handleMotionChange);
      this.abortController?.abort();
      this.abortController = null;
      this.animationFrame = null;
      this.lastFrameTime = null;
    }

    setupLoop() {
      this.querySelectorAll('[data-workshop-clone]').forEach((clone) => clone.remove());

      const beforeClones = document.createDocumentFragment();
      const afterClones = document.createDocumentFragment();
      this.slides.forEach((slide) => {
        beforeClones.append(this.createClone(slide));
        afterClones.append(this.createClone(slide));
      });
      this.track.prepend(beforeClones);
      this.track.append(afterClones);
      this.classList.add('is-looping');
    }

    createClone(slide) {
      const clone = slide.cloneNode(true);
      clone.removeAttribute('data-workshop-slide');
      clone.setAttribute('data-workshop-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('inert', '');

      [clone, ...clone.querySelectorAll('*')].forEach((element) => {
        element.removeAttribute('id');
        element.removeAttribute('tabindex');
        Array.from(element.attributes).forEach((attribute) => {
          if (attribute.name.startsWith('data-shopify')) element.removeAttribute(attribute.name);
        });
      });

      return clone;
    }

    getRenderedSlides() {
      return Array.from(this.track.children).filter((element) =>
        element.matches('[data-workshop-slide], [data-workshop-clone]')
      );
    }

    measureSlideDistance() {
      const renderedSlides = this.getRenderedSlides();
      if (renderedSlides.length < 2) return renderedSlides[0]?.getBoundingClientRect().width || 0;

      return Math.abs(renderedSlides[1].offsetLeft - renderedSlides[0].offsetLeft);
    }

    refreshMeasurements() {
      this.slideDistance = this.measureSlideDistance();
      this.loopSpan = this.isLooping ? this.slideDistance * this.slides.length : 0;
      return this.slideDistance;
    }

    setLoopStart() {
      const span = this.getLoopSpan();
      if (!span) return;

      this.virtualPosition = span;
      this.track.scrollLeft = span;
    }

    getSlideDistance() {
      return this.slideDistance || this.refreshMeasurements();
    }

    getLoopSpan() {
      return this.loopSpan || (this.isLooping ? this.getSlideDistance() * this.slides.length : 0);
    }

    normalizePosition() {
      if (!this.isLooping) return false;

      const span = this.getLoopSpan();
      if (!span) return false;

      let position = this.virtualPosition;
      while (position >= span * 2) position -= span;
      while (position < span) position += span;
      const wrapped = Math.abs(position - this.virtualPosition) > 0.5;
      this.virtualPosition = position;
      if (wrapped) this.track.scrollLeft = position;
      return wrapped;
    }

    setPaused(reason, paused) {
      if (reason === 'interaction' && !paused && this.track) {
        this.virtualPosition = this.track.scrollLeft;
        this.normalizePosition();
      }

      if (paused) this.pauseReasons.add(reason);
      else this.pauseReasons.delete(reason);

      if (this.pauseReasons.size) {
        this.stop();
        return;
      }

      this.start();
    }

    canAutoplay() {
      return Boolean(
        this.isLooping &&
          this.speed &&
          this.slides.length > 1 &&
          !this.pauseReasons.size &&
          !document.hidden &&
          !this.reducedMotionQuery.matches
      );
    }

    start() {
      if (!this.canAutoplay() || this.animationFrame) return;

      this.classList.add('is-autoplaying');
      this.animationFrame = window.requestAnimationFrame((timestamp) => this.tick(timestamp));
    }

    stop() {
      window.cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
      this.lastFrameTime = null;
      this.classList?.remove('is-autoplaying');
    }

    tick(timestamp) {
      this.animationFrame = null;
      if (!this.canAutoplay() || !this.isConnected) return;

      this.advanceAutoplay(timestamp);
      this.animationFrame = window.requestAnimationFrame((nextTimestamp) => this.tick(nextTimestamp));
    }

    advanceAutoplay(timestamp) {
      if (this.lastFrameTime !== null) {
        const elapsed = Math.min(64, Math.max(0, timestamp - this.lastFrameTime));
        this.virtualPosition += this.speed * (elapsed / 1000);
        this.normalizePosition();
        this.track.scrollLeft = this.virtualPosition;
      }

      this.lastFrameTime = timestamp;
    }

    onPointerDown(event) {
      if (!event.isPrimary) return;

      window.clearTimeout(this.resumeTimer);
      this.setPaused('interaction', true);
      if (event.pointerType !== 'mouse') return;

      this.dragState = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startScrollLeft: this.track.scrollLeft,
      };
      this.track.setPointerCapture(event.pointerId);
      this.track.classList.add('is-dragging');
      event.preventDefault();
    }

    onPointerMove(event) {
      if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;

      const span = this.getLoopSpan();
      let nextPosition = this.dragState.startScrollLeft - (event.clientX - this.dragState.startX);
      if (span && nextPosition < 0) nextPosition += span;
      this.virtualPosition = nextPosition;
      this.normalizePosition();
      this.track.scrollLeft = this.virtualPosition;
      event.preventDefault();
    }

    onPointerEnd(event) {
      if (this.dragState && event.pointerId === this.dragState.pointerId) {
        if (this.track.hasPointerCapture?.(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
        this.dragState = null;
        this.track.classList.remove('is-dragging');
      }

      window.clearTimeout(this.resumeTimer);
      this.resumeTimer = window.setTimeout(() => this.setPaused('interaction', false), 1000);
    }

    onWheel() {
      window.clearTimeout(this.resumeTimer);
      this.setPaused('interaction', true);
      this.resumeTimer = window.setTimeout(() => this.setPaused('interaction', false), 1000);
    }

    queueUpdate() {
      if (this.animationFrame && this.canAutoplay()) return;

      window.cancelAnimationFrame(this.updateFrame);
      this.updateFrame = window.requestAnimationFrame(() => this.update());
    }

    update() {
      const distance = Math.max(1, this.getSlideDistance());
      const rawIndex = Math.round(this.track.scrollLeft / distance);
      const firstVisible = this.isLooping
        ? ((rawIndex % this.slides.length) + this.slides.length) % this.slides.length
        : Math.min(this.slides.length - 1, Math.max(0, rawIndex));
      const visibleCount = Math.max(1, Math.round(this.track.clientWidth / distance));
      const lastVisible = Math.min(this.slides.length, firstVisible + visibleCount);

      if (this.status) {
        this.status.textContent = `Showing items ${firstVisible + 1} to ${lastVisible} of ${this.slides.length}`;
      }
    }
  }

  customElements.define('islander-workshop-carousel', IslanderWorkshopCarousel);

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('islander-workshop-carousel').forEach((carousel) => carousel.connectedCallback());
  });
}
